// ─────────────────────────────────────────────
//  TimelineOnCallCard — "who is on call in OB", in the timeline's left rail.
//
//  Sits ABOVE MD CALL (product owner, 2026-08-17). The rail is ordered trust →
//  alarms → reference: OB coverage is a rule the board actively enforces, and a
//  call list is reference, so this goes first of the two.
//
//  ⚠️ IT INVENTS NO STORE. The on-call CRNA already IS an assignment row with
//  `isOnCall: true` (src/domain/coverage.js), carrying `orRoom`, `ahpId`,
//  `ahpName`, `startTime`, `endTime`. This card reads those rows; the edit
//  writes them back through the SAME pair AssignmentModal uses.
//
//  ── WHY A SPLIT IS SAFE TO REPRESENT AS TWO ROWS ──────────────────────────
//  `useControlPanelData` (~278) loops over EVERY `isOnCall` assignment and runs
//  `coverageStatus` on each independently, so a second on-call row for one room
//  is evaluated on its own merits — the "OB must always be covered" rule keeps
//  working for both halves of a split day. `domain/timeline` iterates
//  assignments too, so the chart draws both bars.
//
//  ⚠️ THE ONE PLACE IT DOES NOT WORK, SAID OUT LOUD RATHER THAN HIDDEN:
//  `assignmentForRoom` (domain/gridTemplate.js:151) is `list.find(...)` — the
//  FIRST match — and the Daily List grid and the room card both read through it.
//  With two on-call rows they show only the first. That is the data model, not
//  something this card can fix, so the edit modal states it in one sentence,
//  exactly as AssignmentModal does for its "save as a new case" path.
//
//  Split out into three files for the 500-line rule, the same way this folder
//  already splits TimelineMdCallRail → MdCallEditControls:
//    onCallWindows.js   the rows + the 24-hour coverage maths (pure)
//    OnCallEditModal.js the edit and its writes
//    this file          the read-only rail card
// ─────────────────────────────────────────────
import React, { useMemo, useState } from "react";
import tokens from "../../theme/tokens";
import OnCallEditModal from "./OnCallEditModal";
import {
  allOnCallRows,
  isFullDay,
  onCallRowsFor,
  primaryOnCallRoom,
  sameRoom,
  windowLabel,
} from "./onCallWindows";

const { color, font, radius } = tokens;

export default function TimelineOnCallCard({
  assignments = [],
  rooms = [],
  providers = [],
  facilityId = "default",
  date = "",
  nowMin = null,
  canManageAssignments = false,
}) {
  const [open, setOpen] = useState(false);

  const roomName = useMemo(
    () => primaryOnCallRoom(assignments, rooms),
    [assignments, rooms]
  );
  // Every on-call row for this room, in board order — this is the write order.
  const slotRows = useMemo(
    () => onCallRowsFor(roomName, assignments),
    [roomName, assignments]
  );
  // ⚠️ A ROW WITH NO `ahpId` IS NOBODY. `coverageStatus` ignores it too
  // (coverage.js:18), so an empty slot must read as "nobody on call" and not as
  // a nameless person standing in OB. The row itself is still kept above, so an
  // edit UPDATES it rather than creating a duplicate beside it.
  const staffed = slotRows.filter((r) => r.ahpId);
  // On-call rows for OTHER rooms. Rare, but dropping them would hide a real
  // assignment; one muted line makes it visible without giving this card a
  // second room to edit.
  const elsewhere = allOnCallRows(assignments).filter(
    (a) => a.ahpId && !sameRoom(a.orRoom, roomName)
  );

  // The window is what lets a reader tell who is on RIGHT NOW, so it shows
  // whenever there is a choice to make (two rows) or whenever a single row is
  // not simply the whole day. With one CRNA on a 24h shift, "7 AM–7 AM" beside
  // their name is noise.
  const showWindows = staffed.length > 1 || (staffed.length === 1 && !isFullDay(staffed[0]));

  return (
    <section style={s.card} data-testid="rail-on-call">
      <div style={s.head}>
        <div style={s.label}>CRNA ON CALL</div>
        {/* The SAME permission every other write affordance on this page is
            gated on. A read-only viewer gets no button at all. */}
        {canManageAssignments && (
          <button type="button" style={s.editBtn} onClick={() => setOpen(true)}>
            Edit
          </button>
        )}
      </div>

      {!roomName ? (
        // No on-call row AND no OB-looking room. Saying which room is missing
        // would be inventing one.
        <p style={s.empty}>No on-call room on today's board.</p>
      ) : staffed.length === 0 ? (
        <>
          <div style={s.roomLine}>{roomName}</div>
          {/* ⚠️ NAMED, NOT BLANK. An empty card and a broken card look
              identical, and this is the room the board asserts must always be
              covered — "nothing here" is the one reading it must never allow. */}
          <p style={s.empty}>Nobody on call for {roomName} today.</p>
        </>
      ) : (
        <>
          <div style={s.roomLine}>{roomName}</div>
          <ul style={s.list} aria-label={`On-call CRNAs for ${roomName}`}>
            {staffed.map((r) => (
              <li key={r.id} style={s.row}>
                <span style={s.name} title={r.ahpName}>
                  {r.ahpName}
                </span>
                {showWindows && windowLabel(r) && (
                  <span style={s.meta}>{windowLabel(r)}</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {elsewhere.length > 0 && (
        <p style={s.also}>
          Also on call:{" "}
          {elsewhere.map((a) => `${a.ahpName || "Someone"} (${a.orRoom})`).join(", ")}.
        </p>
      )}

      {open && (
        <OnCallEditModal
          onClose={() => setOpen(false)}
          roomName={roomName}
          rooms={rooms}
          slotRows={slotRows}
          providers={providers}
          facilityId={facilityId}
          date={date}
          nowMin={nowMin}
        />
      )}
    </section>
  );
}

// Rail language — the same card shell, 9px label and letter-spacing
// TimelineMdCallRail uses, so this reads as part of that column rather than a
// panel that wandered in from another page.
const s = {
  card: {
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    padding: "9px 10px 10px",
    minWidth: 0,
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    marginBottom: 7,
  },
  label: {
    fontSize: 9,
    fontWeight: font.weight.bold,
    letterSpacing: ".7px",
    color: color.text.muted,
  },
  // 44px both ways, matching the MD CALL rail's Edit. Outlined, not filled:
  // opening an edit form is not an emergency, and colour weight here would
  // compete with the strips that are.
  editBtn: {
    minWidth: 44,
    minHeight: 44,
    padding: "0 10px",
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: ".4px",
    fontFamily: "inherit",
    color: color.accent.primary,
    background: "transparent",
    border: `1px solid ${color.accent.primary}`,
    borderRadius: radius.sm,
    cursor: "pointer",
    flexShrink: 0,
  },
  // The room once, above the names — not a tag on every row. With two CRNAs a
  // repeated "OB" is noise in a 179px column.
  roomLine: {
    fontSize: 10,
    fontWeight: font.weight.bold,
    letterSpacing: ".4px",
    color: color.accent.primary,
    marginBottom: 3,
  },
  list: { listStyle: "none", margin: 0, padding: 0 },
  // ⚠️ COLUMN, NOT ROW. The window goes on its OWN line under the name — the
  // pattern TimelineMdCallRail's meta row uses — because a 179px track cannot
  // hold "Kayla Gibbons" and "3 PM–7 AM" side by side without truncating the
  // name, and the name is the half that matters.
  row: {
    display: "flex",
    flexDirection: "column",
    padding: "5px 0",
    borderTop: `1px solid ${color.border.default}`,
    minWidth: 0,
  },
  name: {
    fontSize: 11.5,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    lineHeight: 1.25,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  meta: { fontSize: 9, color: color.text.muted, marginTop: 2, whiteSpace: "nowrap" },
  empty: { fontSize: 10, color: color.text.muted, fontStyle: "italic", margin: 0 },
  also: { fontSize: 9, color: color.text.muted, margin: "6px 0 0", lineHeight: 1.35 },
};
